import {gql} from '@apollo/client';
import React from 'react';

import {RELEVANT_PROCESS_FRAGMENT} from 'relevant-items/RelevantItemsSection';
import {ContainerRelevantItemsGraphQL} from 'samples/containers/__generated/ContainerRelevantItemsGraphQL';
import getTestProps from 'test/lib/cypress/getTestProps';

import ContainerRelevantItemsSectionHeader, {
  containerRelevantItemsFilteredTableState,
} from './ContainerRelevantItemsSectionHeader';
import ContainerRelevantItemsSectionTable from './ContainerRelevantItemsSectionTable';

export const CONTAINER_RELEVANT_ITEMS_FRAGMENT = gql`
  fragment ContainerRelevantItemsGraphQL on RelevantItemsNode {
    tagged {
      ... on FolderItemInterface {
        id
        name
        folder {
          id
          name
          source {
            id
            name
          }
        }
        modifiedAt
      }
      ... on StageEntryNode {
        id
        name
        modifiedAt
      }
      ... on EntryLikeDocumentNode {
        id
        name
        modifiedAt
        editURL
        parent {
          ... on WorksheetNode {
            id
            folder {
              id
              source {
                id
                name
              }
            }
          }
        }
        parentId
      }
    }
    filesMentioning {
      ... on FolderItemInterface {
        id
        name
        modifiedAt
        folder {
          id
          source {
            id
            name
          }
        }
      }
      ... on StageEntryNode {
        id
        name
        modifiedAt
      }
      ... on EntryLikeDocumentNode {
        id
        name
        modifiedAt
        editURL
        parent {
          ... on WorksheetNode {
            id
            folder {
              id
              source {
                id
                name
              }
            }
          }
        }
        parentId
      }
    }
    processTasks {
      id
      name
      process {
        ...RelevantProcessGraphQL
      }
    }
    processOutputs {
      id
      name
      process {
        ...RelevantProcessGraphQL
      }
    }
  }
  ${RELEVANT_PROCESS_FRAGMENT}
`;

// hard coded empty query result placeholder for rendering purposes.
const EMPTY_CONTAINER_RELEVANT_ITEMS: ContainerRelevantItemsGraphQL = {
  __typename: 'RelevantItemsNode',
  tagged: [],
  filesMentioning: [],
  processTasks: [],
  processOutputs: [],
};

interface ContainerRelevantItemsSectionProps {
  containerId: string;
}

interface ContainerRelevantItemsSectionContentProps {
  relevantItemsGraphQL: ContainerRelevantItemsGraphQL;
  containerId: string;
}

function ContainerRelevantItemsSectionContent({
  relevantItemsGraphQL,
  containerId,
}: ContainerRelevantItemsSectionContentProps): JSX.Element {
  const [filteredTableState, setFilteredTableState] =
    containerRelevantItemsFilteredTableState.useGlobalClientState(containerId);

  return (
    <div {...getTestProps({component: 'ContainerRelevantItemsSection', element: 'root'})}>
      <ContainerRelevantItemsSectionHeader
        filteredTableState={filteredTableState}
        setFilteredTableState={setFilteredTableState}
      />

      <ContainerRelevantItemsSectionTable
        relevantItemsGraphQL={relevantItemsGraphQL}
        filteredTableState={filteredTableState}
        setFilteredTableState={setFilteredTableState}
        containerId={containerId}
      />
    </div>
  );
}

export default function ContainerRelevantItemsSection({
  containerId,
}: ContainerRelevantItemsSectionProps): JSX.Element {
  // TODO(BNCH-160402): Add in container relevant items node/query instead of placeholder.
  return (
    <ContainerRelevantItemsSectionContent
      relevantItemsGraphQL={EMPTY_CONTAINER_RELEVANT_ITEMS}
      containerId={containerId}
    />
  );
}
